// Which screen the user is on, in words the assistant can use.
//
// Scope is deliberate: this describes the SCREEN, never the document. The
// assistant's knowledge base is the product documentation — it cannot look an
// invoice up, so a vendor name or invoice number would not improve a single
// answer. It would, though, put customer financial data into a third-party
// Hugging Face Space and into the "unanswered question" emails that Space sends
// to support. Screen context is the granularity that actually helps, and it is
// the only granularity that is free to send.
//
// What it buys: "how do I do this?" and "what does this tab mean?" stop being
// unanswerable, because the assistant knows what "this" refers to.

const ROUTE_LABELS = [
  [/^\/canvas\//, 'the document viewer (Canvas), where a document is coded, annotated and approved'],
  [/^\/details\//, 'the document details page'],
  [/^\/generatePo/, 'the Create PO screen'],
  [/^\/users$/, 'the User Management page'],
  [/^\/userProfile$/, 'the Settings page'],
  [/^\/search$/, 'Advanced Search'],
  [/^\/statements-reconciliation$/, 'Statement Reconciliation'],
  [/^\/job-budgets$/, 'Job Budgets'],
  [/^\/vendor$/, 'the Vendor portal'],
  [/^\/admin$/, 'the Admin dashboard']
];

// Longest context we will send. The value lands in the assistant's system
// prompt, so it is capped rather than trusted to stay short.
const MAX_LENGTH = 200;

/**
 * @param {string} pathname   current route
 * @param {object} tabs       state.tabs.tabs — { activeTab, documentChildActiveTab }
 * @returns {string} a short description, or '' when we have nothing useful to say
 */
export const describeAssistantContext = (pathname = '', tabs = {}) => {
  let label = '';

  if (pathname === '/home') {
    // /home is the app shell: the nav tab says which screen is really showing,
    // and the Documents tab has a second level (To Review, In Q&A, On Hold...)
    // that is usually the actual subject of the question.
    const active = tabs?.activeTab;
    const child = tabs?.documentChildActiveTab;

    if (active === 'Documents' && child) {
      label = `the Documents list, on the "${child}" tab`;
    } else if (active) {
      label = `the ${active} page`;
    }
    // No active tab means the tabs slice has not been populated yet, and /home
    // renders a different screen depending on role — Documents for admins, the
    // Dashboard for everyone else (topNav.jsx). Guessing "Documents" told a
    // Controller sitting on the Dashboard that they were somewhere else, and a
    // confidently wrong screen is worse than none: the assistant answers about
    // a page that is not in front of them. Say nothing instead.
  } else {
    const match = ROUTE_LABELS.find(([pattern]) => pattern.test(pathname));
    if (match) label = match[1];
  }

  return label.replace(/\s+/g, ' ').trim().slice(0, MAX_LENGTH);
};
